import { type FC, type ReactNode, memo, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';

import { SbSharedContentModuleBannerText } from './SbSharedContentModuleBannerText';
import { SbSharedContentModuleBannerImage } from './SbSharedContentModuleBannerImage';
import { trackInteraction } from './SbSharedContentModuleBannerTiles';

import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

import { type SbSharedContentModuleBannerItem_banner$key } from './__generated__/SbSharedContentModuleBannerItem_banner.graphql';

import dibsCss from 'dibs-css';
import styles from './SbSharedContentModuleBannerItem.scss';

type Props = {
    isSalePage: boolean;
    termsLink: string;
    banner: SbSharedContentModuleBannerItem_banner$key | null | undefined;
    renderImage: ({
        src,
        alt,
        className,
        srcSet,
        sizes,
    }: {
        src: string;
        alt: string;
        className: string;
        srcSet: string;
        sizes?: string;
    }) => ReactNode;
    useH1Header: boolean;
};

export const SbSharedContentModuleBannerItem: FC<Props> = memo(
    ({ banner: bannerRef, renderImage, termsLink, isSalePage, useH1Header }) => {
        const { isMobile } = useServerVarsContext();
        const banner = useFragment(
            graphql`
                fragment SbSharedContentModuleBannerItem_banner on BrowseContentModuleBannerType {
                    ...SbSharedContentModuleBannerText_banner
                    ...SbSharedContentModuleBannerImage_banner
                    format
                    ctaUrl
                    textPosition
                    backgroundColor
                    bannerImageWidth
                }
            `,
            bannerRef
        );
        const { format, textPosition, backgroundColor, bannerImageWidth, ctaUrl } = banner || {};

        const isStaticBanner = format === 'BURN_IN' && !isMobile;
        const isHalfWidth = bannerImageWidth === 'HALF' || !isStaticBanner;
        const showBackgroundColor = isHalfWidth && !!backgroundColor;

        const wrapperClasses = classnames(
            styles.wrapper,
            dibsCss.relative,
            dibsCss.flex,
            dibsCss.justifyBetween,
            {
                [styles.reverse]: textPosition === 'right',
                [styles.border]: !backgroundColor && !isHalfWidth,
            }
        );

        useEffect(() => {
            if (isSalePage) {
                trackInteraction('sale banner carousel');
            }
        }, [isSalePage]);

        if (isStaticBanner) {
            return (
                <div className={classnames(dibsCss.relative, dibsCss.block, dibsCss.maxW1440px)}>
                    <div className={wrapperClasses}>
                        <div className={classnames(dibsCss.absolute, dibsCss.hFull, dibsCss.w40)}>
                            <SbSharedContentModuleBannerText
                                banner={banner || null}
                                useH1Header={useH1Header}
                                isStaticBanner={isStaticBanner}
                            />
                        </div>
                        <SbSharedContentModuleBannerImage
                            renderImage={renderImage}
                            banner={banner || null}
                            isStaticBanner={isStaticBanner}
                            isHalfWidth={isHalfWidth}
                        />
                    </div>
                </div>
            );
        }
        return (
            <div
                className={classnames(dibsCss.relative, dibsCss.maxW1440px, {
                    [styles.border]: !backgroundColor && (isHalfWidth || isMobile),
                })}
                style={{
                    backgroundColor: showBackgroundColor ? backgroundColor : undefined,
                }}
            >
                <div className={wrapperClasses}>
                    <>
                        <SbSharedContentModuleBannerText
                            banner={banner || null}
                            isStaticBanner={isStaticBanner}
                            useH1Header={useH1Header}
                        />
                        {termsLink && !ctaUrl && (
                            <Link href={termsLink} className={styles.termsLink}>
                                <FormattedMessage
                                    id="abf.SbSharedContentModuleBannerTiles.termsLink"
                                    defaultMessage="Terms & Conditions"
                                />
                            </Link>
                        )}
                    </>

                    <SbSharedContentModuleBannerImage
                        renderImage={renderImage}
                        banner={banner || null}
                        isStaticBanner={isStaticBanner}
                        isHalfWidth={isHalfWidth}
                    />
                </div>
            </div>
        );
    }
);
